import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNetworkStore } from '../store/networkStore';
import { 
  Track, 
  TrackElement, 
  TrackElementType,
  DEFAULT_TRACK,
} from '@shared';
import './TrackEditor.css';

interface Point {
  x: number;
  y: number;
}

const ELEMENT_TYPES: { type: TrackElementType; label: string; icon: string }[] = [
  { type: 'road', label: 'Road', icon: '🛣️' },
  { type: 'wall', label: 'Wall', icon: '🧱' },
  { type: 'checkpoint', label: 'Checkpoint', icon: '🚩' },
  { type: 'finish', label: 'Finish Line', icon: '🏁' },
  { type: 'boost', label: 'Boost Pad', icon: '⚡' },
  { type: 'oil', label: 'Oil Slick', icon: '🛢️' },
  { type: 'spawn', label: 'Spawn Point', icon: '🚗' },
  { type: 'bridge', label: 'Bridge', icon: '🌉' },
  { type: 'ramp_up', label: 'Ramp Up', icon: '⬆️' },
  { type: 'ramp_down', label: 'Ramp Down', icon: '⬇️' },
];

function TrackEditor() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { trackList, requestTrackList, socket, connected } = useNetworkStore();
  
  const [track, setTrack] = useState<Track>({ ...DEFAULT_TRACK });
  const [selectedTool, setSelectedTool] = useState<TrackElementType>('road');
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState<Point[]>([]);
  const [gridSize, setGridSize] = useState(20);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [currentLayer, setCurrentLayer] = useState(0); // Layer for new elements

  // Request track list when connected (or when connected changes to true)
  useEffect(() => {
    if (connected) {
      requestTrackList();
    }
  }, [connected, requestTrackList]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#2a2a4e';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < track.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, track.height);
        ctx.stroke();
      }
      for (let y = 0; y < track.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(track.width, y);
        ctx.stroke();
      }
    }

    // Draw elements
    track.elements.forEach((element) => {
      const isSelected = element.id === selectedElement;
      
      switch (element.type) {
        case 'road':
          ctx.fillStyle = isSelected ? '#4a4a6e' : '#3a3a5e';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          break;
        case 'wall':
          ctx.fillStyle = isSelected ? '#8b0000' : '#4a0000';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          break;
        case 'checkpoint':
          ctx.fillStyle = isSelected ? 'rgba(255, 255, 0, 0.5)' : 'rgba(255, 255, 0, 0.3)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.strokeStyle = '#ffff00';
          ctx.lineWidth = 2;
          ctx.strokeRect(element.x, element.y, element.width, element.height);
          break;
        case 'finish':
          // Checkered pattern
          const squareSize = 10;
          for (let i = 0; i < element.width; i += squareSize) {
            for (let j = 0; j < element.height; j += squareSize) {
              ctx.fillStyle = (Math.floor(i / squareSize) + Math.floor(j / squareSize)) % 2 === 0 
                ? 'white' : 'black';
              ctx.fillRect(element.x + i, element.y + j, squareSize, squareSize);
            }
          }
          break;
        case 'boost':
          ctx.fillStyle = isSelected ? 'rgba(0, 255, 255, 0.6)' : 'rgba(0, 255, 255, 0.4)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          // Arrow
          ctx.fillStyle = 'white';
          ctx.font = '20px Arial';
          ctx.fillText('→', element.x + element.width / 2 - 10, element.y + element.height / 2 + 7);
          break;
        case 'oil':
          ctx.fillStyle = isSelected ? 'rgba(50, 50, 50, 0.8)' : 'rgba(30, 30, 30, 0.7)';
          ctx.beginPath();
          ctx.ellipse(
            element.x + element.width / 2,
            element.y + element.height / 2,
            element.width / 2,
            element.height / 2,
            0, 0, Math.PI * 2
          );
          ctx.fill();
          break;
        case 'spawn':
          ctx.fillStyle = isSelected ? 'rgba(0, 255, 0, 0.6)' : 'rgba(0, 255, 0, 0.4)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.fillStyle = 'white';
          ctx.font = '16px Arial';
          ctx.fillText('🚗', element.x + 5, element.y + element.height / 2 + 5);
          break;
        case 'bridge':
          // Bridge - elevated road, shown with distinct color and border
          ctx.fillStyle = isSelected ? 'rgba(139, 90, 43, 0.8)' : 'rgba(139, 69, 19, 0.7)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.strokeStyle = '#8b4513';
          ctx.lineWidth = 3;
          ctx.strokeRect(element.x, element.y, element.width, element.height);
          // Layer indicator
          ctx.fillStyle = 'white';
          ctx.font = '12px Arial';
          ctx.fillText(`L${element.layer ?? 1}`, element.x + 4, element.y + 14);
          break;
        case 'ramp_up':
          // Ramp going up - arrow pointing up
          ctx.fillStyle = isSelected ? 'rgba(100, 200, 100, 0.7)' : 'rgba(80, 180, 80, 0.6)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.fillStyle = 'white';
          ctx.font = '24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('⬆', element.x + element.width / 2, element.y + element.height / 2 + 8);
          ctx.textAlign = 'left';
          break;
        case 'ramp_down':
          // Ramp going down - arrow pointing down
          ctx.fillStyle = isSelected ? 'rgba(200, 100, 100, 0.7)' : 'rgba(180, 80, 80, 0.6)';
          ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.fillStyle = 'white';
          ctx.font = '24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('⬇', element.x + element.width / 2, element.y + element.height / 2 + 8);
          ctx.textAlign = 'left';
          break;
      }
      
      // Draw layer indicator for elevated elements (layer > 0)
      if ((element.layer ?? 0) !== 0 && element.type !== 'bridge') {
        ctx.strokeStyle = element.layer && element.layer > 0 ? '#00ff00' : '#ff6600';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 2]);
        ctx.strokeRect(element.x, element.y, element.width, element.height);
        ctx.setLineDash([]);
      }
    });

    // Draw current drawing
    if (isDrawing && drawPoints.length > 0) {
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      
      const start = drawPoints[0];
      const end = drawPoints[drawPoints.length - 1];
      if (start && end) {
        ctx.strokeRect(
          Math.min(start.x, end.x),
          Math.min(start.y, end.y),
          Math.abs(end.x - start.x),
          Math.abs(end.y - start.y)
        );
      }
      ctx.setLineDash([]);
    }

    ctx.restore();
  }, [track, selectedElement, showGrid, gridSize, zoom, pan, isDrawing, drawPoints]);

  const snapToGrid = (value: number): number => {
    return Math.round(value / gridSize) * gridSize;
  };

  const getCanvasPoint = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: snapToGrid((e.clientX - rect.left - pan.x) / zoom),
      y: snapToGrid((e.clientY - rect.top - pan.y) / zoom),
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasPoint(e);
    setIsDrawing(true);
    setDrawPoints([point]);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !drawPoints[0]) return;
    const point = getCanvasPoint(e);
    setDrawPoints([drawPoints[0], point]);
  };

  const handleMouseUp = () => {
    if (isDrawing && drawPoints.length >= 2) {
      const start = drawPoints[0];
      const end = drawPoints[drawPoints.length - 1];
      
      if (!start || !end) {
        setIsDrawing(false);
        setDrawPoints([]);
        return;
      }
      
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      
      // Determine layer based on element type
      let elementLayer = currentLayer;
      if (selectedTool === 'bridge') {
        elementLayer = 1; // Bridges are always on layer 1
      } else if (selectedTool === 'ramp_up' || selectedTool === 'ramp_down') {
        elementLayer = 0; // Ramps start at ground level
      }
      
      const newElement: TrackElement = {
        id: `element-${Date.now()}`,
        type: selectedTool,
        x,
        y,
        position: { x, y },
        width: Math.abs(end.x - start.x) || gridSize,
        height: Math.abs(end.y - start.y) || gridSize,
        rotation: 0,
        layer: elementLayer,
      };

      if (selectedTool === 'checkpoint') {
        newElement.checkpointIndex = track.elements.filter(e => e.type === 'checkpoint').length;
      }

      setTrack(prev => ({
        ...prev,
        elements: [...prev.elements, newElement],
      }));
    }
    
    setIsDrawing(false);
    setDrawPoints([]);
  };

  const handleDeleteElement = () => {
    if (!selectedElement) return;
    setTrack(prev => ({
      ...prev,
      elements: prev.elements.filter(e => e.id !== selectedElement),
    }));
    setSelectedElement(null);
  };

  const handleSaveTrack = async () => {
    try {
      const response = await fetch('/api/tracks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(track),
      });
      
      if (response.ok) {
        alert('Track saved successfully!');
        requestTrackList();
      } else {
        const data = await response.json();
        const errorMessages = data.errors?.map((e: { message: string }) => e.message).join('\n') || 'Unknown error';
        alert(`Failed to save track:\n${errorMessages}`);
      }
    } catch (error) {
      console.error('Error saving track:', error);
      alert('Error saving track');
    }
  };

  const handleLoadTrack = async (trackId: string) => {
    console.log('Loading track:', trackId);
    try {
      const response = await fetch(`/api/tracks/${trackId}`);
      console.log('Load track response status:', response.status);
      if (response.ok) {
        const trackData = await response.json();
        console.log('Loaded track data:', trackData.name, trackData.elements?.length, 'elements');
        setTrack(trackData);
        setSelectedElement(null);
      } else {
        console.error('Failed to load track, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Failed to load track:', error);
    }
  };

  const handleNewTrack = () => {
    setTrack({
      ...DEFAULT_TRACK,
      id: `track-${Date.now()}`,
      name: 'New Track',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    setSelectedElement(null);
  };

  const handleDeleteTrack = async (trackId: string, trackName: string) => {
    if (!confirm(`Are you sure you want to delete "${trackName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/tracks/${trackId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Track deleted successfully!');
        requestTrackList();
        // If we deleted the currently loaded track, create a new one
        if (track.id === trackId) {
          handleNewTrack();
        }
      } else {
        const data = await response.json();
        alert(`Failed to delete track: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      alert('Error deleting track');
    }
  };

  const handleCopyTrack = async (trackId: string) => {
    console.log('Copying track:', trackId);
    try {
      const response = await fetch(`/api/tracks/${trackId}`);
      console.log('Copy track fetch response status:', response.status);
      if (response.ok) {
        const trackData = await response.json();
        console.log('Fetched track for copy:', trackData.name);
        // Create a copy with new ID and modified name
        const newTrack = {
          ...trackData,
          id: `track-${Date.now()}`,
          name: `${trackData.name} (Copy)`,
          author: trackData.author === 'System' ? '' : trackData.author,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        console.log('Created copy with new ID:', newTrack.id);
        setTrack(newTrack);
        setSelectedElement(null);
      } else {
        console.error('Failed to fetch track for copy, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        alert('Failed to copy track');
      }
    } catch (error) {
      console.error('Failed to copy track:', error);
      alert('Failed to copy track');
    }
  };

  return (
    <div className="screen track-editor">
      <header className="header">
        <button className="btn btn-ghost" onClick={() => navigate('/')}>
          ← Back
        </button>
        <h1>Track Editor</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleNewTrack}>
            New
          </button>
          <button className="btn btn-primary" onClick={handleSaveTrack}>
            Save
          </button>
        </div>
      </header>

      <div className="editor-layout">
        {/* Toolbar */}
        <aside className="toolbar card">
          <h3>Tools</h3>
          <div className="tool-list">
            {ELEMENT_TYPES.map((tool) => (
              <button
                key={tool.type}
                className={`tool-btn ${selectedTool === tool.type ? 'active' : ''}`}
                onClick={() => setSelectedTool(tool.type)}
              >
                <span className="tool-icon">{tool.icon}</span>
                <span className="tool-label">{tool.label}</span>
              </button>
            ))}
          </div>

          <h3>Track Settings</h3>
          <div className="track-settings">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="input"
                value={track.name}
                onChange={(e) => setTrack(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                className="input"
                value={track.author}
                onChange={(e) => setTrack(prev => ({ ...prev, author: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select
                className="input"
                value={track.difficulty}
                onChange={(e) => setTrack(prev => ({ 
                  ...prev, 
                  difficulty: e.target.value as Track['difficulty']
                }))}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="extreme">Extreme</option>
              </select>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={track.wrapAround ?? false}
                onChange={(e) => setTrack(prev => ({ 
                  ...prev, 
                  wrapAround: e.target.checked 
                }))}
              />
              Infinite Scroll (Wrap-around)
            </label>
          </div>

          <h3>Layer</h3>
          <div className="layer-options">
            <div className="form-group">
              <label>Current Layer</label>
              <select
                className="input"
                value={currentLayer}
                onChange={(e) => setCurrentLayer(Number(e.target.value))}
              >
                <option value={-1}>-1 (Tunnel)</option>
                <option value={0}>0 (Ground)</option>
                <option value={1}>1 (Bridge)</option>
                <option value={2}>2 (Upper Bridge)</option>
              </select>
            </div>
            <p className="layer-hint">
              Elements placed will be on this layer. Bridge elements auto-set to layer 1.
            </p>
          </div>

          <h3>View Options</h3>
          <div className="view-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
              />
              Show Grid
            </label>
            <div className="form-group">
              <label>Grid Size</label>
              <input
                type="range"
                min="10"
                max="50"
                value={gridSize}
                onChange={(e) => setGridSize(Number(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Zoom</label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
          </div>
        </aside>

        {/* Canvas */}
        <main className="canvas-container">
          <canvas
            ref={canvasRef}
            width={1200}
            height={800}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </main>

        {/* Properties Panel */}
        <aside className="properties-panel card">
          <h3>Existing Tracks</h3>
          <div className="track-list-editor">
            {trackList.map((t) => (
              <div key={t.id} className="track-list-item-row">
                <button
                  className={`track-list-item ${track.id === t.id ? 'active' : ''}`}
                  onClick={() => handleLoadTrack(t.id)}
                >
                  {t.name}
                </button>
                <button
                  className="btn btn-secondary btn-small track-copy-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyTrack(t.id);
                  }}
                  title="Copy track"
                >
                  📋
                </button>
                {t.author !== 'System' && t.id !== 'default-oval' && (
                  <button
                    className="btn btn-error btn-small track-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTrack(t.id, t.name);
                    }}
                    title="Delete track"
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>

          {selectedElement && (
            <>
              <h3>Selected Element</h3>
              <div className="element-properties">
                <p>Type: {track.elements.find(e => e.id === selectedElement)?.type}</p>
                <button className="btn btn-error" onClick={handleDeleteElement}>
                  Delete Element
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

export default TrackEditor;
